import { TableCell, Typography } from "@mui/material";
import type { SxProps } from "@mui/material";
import { MouseEventHandler, forwardRef } from "react";
import { headerColor } from "../constants";
import CustomTooltip from "./CustomTooltip";

type Props = {
  children?: any;
  onClick?: MouseEventHandler<HTMLTableCellElement>;
  sx?: SxProps;
  colSpan?: number;
  rowSpan?: number;
  isLineVisible?: boolean;
};


const fontSize = 14;
const defaultRowSpan = 1;
const defaultHeaderRowSpan = 1;

const styles: { [key: string]: SxProps } = {
  multiEllipsis: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    fontSize,
    maxHeight: 44,
    minWidth: 30,
    overflow: "hidden",
    overflowWrap: "break-word",
  },
  lineVisible: {
    borderLeft: "1px solid var(--offwhite-600)",
  },
};

/**
 *
 * @param root0 コンポーネントのプロップス
 */
export default forwardRef(
  ({ children, onClick, sx, colSpan, rowSpan, isLineVisible = true }: Props, ref) => (
    <TableCell
      colSpan={colSpan}
      rowSpan={rowSpan ?? defaultRowSpan}
      ref={ref}
      sx={{
        ...(isLineVisible
          ? {
              borderLeft: "1px solid var(--offwhite-600)",
            }
          : {}),
        fontSize,
        position: "relative",
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "normal",
        maxWidth: 110,
        ...(sx ?? {}),
      }}
      onClick={onClick}
    >
      {children}
    </TableCell>
  ),
);

// 商品名などに使用
export const TableCellWide = forwardRef(
  ({ children, onClick, sx, colSpan, rowSpan, isLineVisible = true }: Props, ref) => (
    <TableCell
      colSpan={colSpan}
      rowSpan={rowSpan ?? defaultRowSpan}
      ref={ref}
      sx={{
        fontSize,
        position: "relative",
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "normal",
        minWidth: 99,
        maxWidth: 160,
        paddingY: 0,
        ...(isLineVisible
          ? {
              borderLeft: "1px solid var(--offwhite-600)",
            }
          : {}),
        ...(sx ?? {}),
      }}
      onClick={onClick}
    >
      {children}
    </TableCell>
  ),
);

// 在庫数などに使用
export const TableCellNarrow = forwardRef(
  ({ children, onClick, sx, colSpan, rowSpan, isLineVisible = true }: Props, ref) => (
    <TableCell
      colSpan={colSpan}
      rowSpan={rowSpan ?? defaultRowSpan}
      ref={ref}
      sx={{
        fontSize,
        position: "relative",
        textOverflow: "ellipsis",
        overflow: "hidden",
        minWidth: 40,
        maxWidth: 50,
        paddingX: 1,
        ...(isLineVisible
          ? {
              borderLeft: "1px solid var(--offwhite-600)",
            }
          : {}),
        ...(sx ?? {}),
      }}
      onClick={onClick}
    >
      {children}
    </TableCell>
  ),
);

// テーブルヘッダー用
export const TableHeaderCellSmall = forwardRef(
  ({ children, onClick, sx, colSpan, rowSpan, isLineVisible = true }: Props, ref) => (
    <TableCell
      colSpan={colSpan}
      rowSpan={rowSpan ?? defaultHeaderRowSpan}
      ref={ref}
      sx={{
        fontSize,
        position: "sticky",
        top: 0,
        zIndex: 5,
        backgroundColor: "var(--white)",
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
        maxWidth: 110,
        ...(isLineVisible
          ? {
              borderLeft: "1px solid var(--offwhite-600)",
              borderTop: "1px solid var(--offwhite-600)",
              borderBottom: "1px solid var(--offwhite-600)",
              backgroundColor: headerColor,
            }
          : {}),
        ...(sx ?? {}),
      }}
      onClick={onClick}
    >
      {children}
    </TableCell>
  ),
);

// テーブルヘッダー用の商品名など幅の大きなものに使用
export const TableHeaderCellWide = forwardRef(
  ({ children, onClick, sx, colSpan, rowSpan, isLineVisible = true }: Props, ref) => (
    <TableCell
      colSpan={colSpan}
      rowSpan={rowSpan ?? defaultHeaderRowSpan}
      ref={ref}
      sx={{
        fontSize,
        position: "sticky",
        top: 0,
        zIndex: 5,
        backgroundColor: "var(--white)",
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "normal",
        minWidth: 50,
        maxWidth: 160,
        paddingY: 0,
        ...(isLineVisible
          ? {
              borderLeft: "1px solid var(--offwhite-600)",
              borderTop: "1px solid var(--offwhite-600)",
              borderBottom: "1px solid var(--offwhite-600)",
              backgroundColor: headerColor,
            }
          : {}),
        ...(sx ?? {}),
      }}
      onClick={onClick}
    >
      {children}
    </TableCell>
  ),
);

// テーブルヘッダー用の在庫数など幅の小さなものに使用
export const TableHeaderCellNarrow = forwardRef(
  ({ children, onClick, sx, colSpan, rowSpan, isLineVisible = true }: Props, ref) => (
    <TableCell
      colSpan={colSpan}
      rowSpan={rowSpan ?? defaultHeaderRowSpan}
      ref={ref}
      sx={{
        fontSize,
        position: "sticky",
        top: 0,
        zIndex: 5,
        backgroundColor: "var(--white)",
        textOverflow: "ellipsis",
        overflow: "hidden",
        minWidth: 40,
        maxWidth: 50,
        paddingX: 1,
        ...(isLineVisible
          ? {
              borderLeft: "1px solid var(--offwhite-600)",
              borderTop: "1px solid var(--offwhite-600)",
              borderBottom: "1px solid var(--offwhite-600)",
              backgroundColor: headerColor,
            }
          : {}),
        ...(sx ?? {}),
      }}
      onClick={onClick}
    >
      {children}
    </TableCell>
  ),
);

/**
 * 指定行入力し、指定行をはみ出した場合はellipsisにするコンポーネント
 * @param children 子コンポーネント
 * @returns JSX.Element
 */
export function MultiTypography({children, sx, noTooltip}: {children: JSX.Element | string | null, sx?: any, noTooltip?: boolean}) {
  if (noTooltip) {
    return <Typography component="span" sx={{...styles.multiEllipsis, ...(sx ?? {})}}>{children}</Typography>;
  }
  return <CustomTooltip title={children} disableInteractive arrow><Typography component="span" sx={{...styles.multiEllipsis, ...(sx ?? {})}}>{children}</Typography></CustomTooltip>;
}
